import type { QueryClient } from "@tanstack/react-query";
import type { LoaderFunction } from "react-router";

import { orgStorage } from "@/shared/auth";
import { FeatureFlagService } from "@/shared/domain/featureFlags";
import { featureFlagQueryKeys } from "@/shared/domain/featureFlags/hooks/featureFlagQueryKeys";
import type { QueryTarget } from "@/shared/domain/query";
import { makeAuthenticatedHttpClient } from "@/shared/http/makeAuthenticatedHttpClient";

import { viewQueryKeys } from "../hooks/viewQueryKeys";
import type { ViewRecord } from "../ViewRecord";
import { VIEW_SEARCH_PARAM } from "../viewSearchParam";
import { ViewService } from "../ViewService";

/** Reads a single View. Injected so the branch tests need no network stack. */
export type FetchView = (viewId: string, orgId: string) => Promise<ViewRecord>;

/** Reads every View on a target. Injected for the same reason. */
export type FetchViews = (
  target: QueryTarget,
  orgId: string,
) => Promise<ViewRecord[]>;

/** Reads the flag matrix. Injected so a test can hold it pending or fail it. */
export type FetchFlagStates = () => Promise<
  Record<string, Record<string, boolean>>
>;

async function fetchViewOverHttp(
  viewId: string,
  orgId: string,
): Promise<ViewRecord> {
  const httpClient = await makeAuthenticatedHttpClient();
  return new ViewService(httpClient).getViewById(viewId, orgId);
}

async function fetchViewsOverHttp(
  target: QueryTarget,
  orgId: string,
): Promise<ViewRecord[]> {
  const httpClient = await makeAuthenticatedHttpClient();
  return new ViewService(httpClient).getAllViews(target, orgId);
}

async function fetchFlagStatesOverHttp(): Promise<
  Record<string, Record<string, boolean>>
> {
  const httpClient = await makeAuthenticatedHttpClient();
  return new FeatureFlagService(httpClient).checkAllFeatureFlags();
}

export interface ViewsLoaderOptions {
  /** Resource type this list shows, and so whose Views the picker offers. */
  target: QueryTarget;
  /** Override the single-View read for tests. */
  fetchView?: FetchView;
  /** Override the list read for tests. */
  fetchViews?: FetchViews;
  /** Override the flag read for tests. */
  fetchFlagStates?: FetchFlagStates;
}

/**
 * Give a resource list everything its View surface needs *before* it paints.
 *
 * Three separate late arrivals used to move the page, and this closes all of
 * them by making the answers available on the first render rather than a beat
 * into it:
 *
 *  1. **The picker button.** `ViewPicker` renders `null` until `release-views`
 *     resolves, and `useCheckFeatureFlag` reads a pending query as `false` —
 *     so the button and its separator used to appear a moment after the toolbar
 *     did, shifting the whole button row. `AuthProvider` starts this fetch but
 *     deliberately does not await it, so on a cold load the list can paint
 *     first. Awaiting it here costs only the wait on a request already in
 *     flight: same key, `staleTime: Infinity`, so TanStack dedupes onto it.
 *  2. **The count badge.** The trigger renders `Views 3`, and the badge is part
 *     of the button's width — so the list arriving late widened the button and
 *     shifted the row a second time. Warmed through the same helper `useViews`
 *     uses, so the seeded list cannot differ from the fetched one.
 *  3. **The linked View.** With `?view=` in the URL, resolving the record here
 *     lets `useLinkedViewSeed` build the table filtered on its first render,
 *     rather than fetching an unfiltered page and replacing it.
 *
 * Always resolves `null`, and never blocks the list on a View failing to load.
 * A View is decoration on a resource list, never the reason the route exists:
 *
 *   - **No current org** — defers to the auth layout, as
 *     `makeFeatureFlagRedirectLoader` does.
 *   - **`release-views` off, or unreadable** — returns without asking for any
 *     View. Every View endpoint 403s while the flag is off, and no View UI
 *     mounts, so there is nothing to warm. Fails closed, matching the route
 *     gate and `useCheckFeatureFlag`.
 *   - **Either read failing** — swallowed. The list renders; `useViews` retries
 *     on mount, and `useViewDeepLink` re-reads the linked View's query and
 *     turns 401 and 404 into the two messages a recipient needs. Throwing would
 *     replace a whole list with an error boundary over a stale parameter.
 */
export function makeViewsLoader(
  queryClient: QueryClient,
  {
    target,
    fetchView = fetchViewOverHttp,
    fetchViews = fetchViewsOverHttp,
    fetchFlagStates = fetchFlagStatesOverHttp,
  }: ViewsLoaderOptions,
): LoaderFunction {
  return async function loadViews({ request }) {
    const orgRecord = orgStorage.getCurrentOrg();
    if (!orgRecord) {
      return null;
    }
    const orgId = orgRecord.org_id;

    let flagStates: Record<string, Record<string, boolean>>;
    try {
      // Same key and `staleTime` as `AuthProvider`'s un-awaited prefetch and as
      // `useCheckFeatureFlag`, so this joins that request rather than making a
      // second one — dedupe is by key, not by function.
      flagStates = await queryClient.fetchQuery({
        queryKey: featureFlagQueryKeys.states(),
        queryFn: fetchFlagStates,
        staleTime: Infinity,
      });
    } catch {
      return null;
    }

    if (!flagStates[orgId]?.["release-views"]) {
      return null;
    }

    const viewId = new URL(request.url).searchParams.get(VIEW_SEARCH_PARAM);

    // Concurrently: the picker's list is needed on every list load, the linked
    // record only when the URL names one. Neither waits on the other.
    await Promise.all([
      queryClient
        .ensureQueryData({
          queryKey: viewQueryKeys.list(orgId, target),
          queryFn: () => fetchViews(target, orgId),
        })
        .catch(() => null),
      viewId === null
        ? Promise.resolve(null)
        : queryClient
            .ensureQueryData({
              queryKey: viewQueryKeys.id(orgId, viewId),
              queryFn: () => fetchView(viewId, orgId),
            })
            .catch(() => null),
    ]);

    return null;
  };
}
